import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { 
  fetchStatuses, 
  createStatus, 
  updateStatus,
  getDefaultWorkflow,
  StatusData,
  StatusWorkflow 
} from "@/lib/stores/statusStore";
import SaveBar from "./SaveBar";
import { Input } from  "@/components/ui/InputDefault";
import { Button } from "@/components/ui/button";
import IconProvider from "@/components/ui/IconProvider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/DefaultSelectDropdown";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

type WorkflowType = 'objects' | 'templates' | 'documents' | 'dialogs' | 'loca';

export default function StatusManagementSection() {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedGame, selectedCompany } = useSimplifiedUserData();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isGlobalMode, setIsGlobalMode] = useState(true);
  const [selectedType, setSelectedType] = useState<WorkflowType>('objects');
  const [statusConfig, setStatusConfig] = useState<StatusData | null>(null);
  const [isGameSpecific, setIsGameSpecific] = useState(false);

  // Current workflow values
  const [workflows, setWorkflows] = useState<Record<WorkflowType, StatusWorkflow>>({
    objects: getDefaultWorkflow(),
    templates: getDefaultWorkflow(),
    documents: getDefaultWorkflow(),
    dialogs: getDefaultWorkflow(),
    loca: getDefaultWorkflow()
  });

  // Original values for change tracking
  const [originalWorkflows, setOriginalWorkflows] = useState<Record<WorkflowType, StatusWorkflow>>({
    objects: getDefaultWorkflow(),
    templates: getDefaultWorkflow(),
    documents: getDefaultWorkflow(),
    dialogs: getDefaultWorkflow(),
    loca: getDefaultWorkflow()
  });

  // Load status configurations
  useEffect(() => {
    const loadStatuses = async () => {
      if (!selectedCompany) return;
      
      setIsLoading(true);
      try {
        const statuses = await fetchStatuses(
          selectedCompany.id,
          selectedGame?.id
        );

        // Find game-specific or company default
        let config: StatusData | null = null;
        
        if (selectedGame) {
          // Look for game-specific config
          config = statuses.find(s => s.game_id === selectedGame.id) || null;
          setIsGameSpecific(!!config);
          
          // If no game-specific, use company default
          if (!config) {
            config = statuses.find(s => s.game_id === null) || null;
          }
        } else {
          // No game selected, use company default
          config = statuses.find(s => s.game_id === null) || null;
          setIsGameSpecific(false);
        }

        if (config) {
          setStatusConfig(config);
          const loadedWorkflows = {
            objects: config.objects || getDefaultWorkflow(),
            templates: config.templates || getDefaultWorkflow(),
            documents: config.documents || getDefaultWorkflow(),
            dialogs: config.dialogs || getDefaultWorkflow(),
            loca: config.loca || getDefaultWorkflow()
          };
          setWorkflows(loadedWorkflows);
          setOriginalWorkflows(JSON.parse(JSON.stringify(loadedWorkflows)));

          // Check if all workflows are identical (global mode)
          const allSame = checkAllWorkflowsIdentical(loadedWorkflows);
          setIsGlobalMode(allSame);
        } else {
          // No config exists, use defaults
          const defaultWorkflows = {
            objects: getDefaultWorkflow(),
            templates: getDefaultWorkflow(),
            documents: getDefaultWorkflow(),
            dialogs: getDefaultWorkflow(),
            loca: getDefaultWorkflow()
          };
          setWorkflows(defaultWorkflows);
          setOriginalWorkflows(JSON.parse(JSON.stringify(defaultWorkflows)));
          setIsGlobalMode(true);
        }
      } catch (error) {
        console.error('Error loading status configurations:', error);
        toast({
          title: "Error",
          description: "Failed to load status configurations",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadStatuses();
  }, [selectedCompany, selectedGame, toast]);

  // Check if all workflows are identical
  const checkAllWorkflowsIdentical = (wf: Record<WorkflowType, StatusWorkflow>): boolean => {
    const types: WorkflowType[] = ['objects', 'templates', 'documents', 'dialogs', 'loca'];
    const first = JSON.stringify(wf[types[0]]);
    return types.every(type => JSON.stringify(wf[type]) === first);
  };

  // Toggle between global and specific mode
  const handleModeToggle = () => {
    if (isGlobalMode) {
      // Switching to specific mode - workflows stay as they are
      setIsGlobalMode(false);
    } else {
      // Switching to global mode - copy first workflow to all
      const globalWorkflow = { ...workflows.objects };
      setWorkflows({
        objects: globalWorkflow,
        templates: { ...globalWorkflow },
        documents: { ...globalWorkflow },
        dialogs: { ...globalWorkflow },
        loca: { ...globalWorkflow }
      });
      setIsGlobalMode(true);
    }
  };

  // Update workflow step
  const updateWorkflowStep = (type: WorkflowType, stepKey: string, field: 'name' | 'color', value: string) => {
    setWorkflows(prev => {
      const newWorkflows = { ...prev };
      
      if (isGlobalMode) {
        // Update all workflows in global mode
        const types: WorkflowType[] = ['objects', 'templates', 'documents', 'dialogs', 'loca'];
        types.forEach(t => {
          const workflow = { ...newWorkflows[t] };
          if (field === 'name') {
            // Rename the key
            const oldValue = workflow[stepKey];
            delete workflow[stepKey];
            workflow[value] = oldValue;
          } else {
            workflow[stepKey] = { ...workflow[stepKey], color: value };
          }
          newWorkflows[t] = workflow;
        });
      } else {
        // Update only the selected type
        const workflow = { ...newWorkflows[type] };
        if (field === 'name') {
          const oldValue = workflow[stepKey];
          delete workflow[stepKey];
          workflow[value] = oldValue;
        } else {
          workflow[stepKey] = { ...workflow[stepKey], color: value };
        }
        newWorkflows[type] = workflow;
      }
      
      return newWorkflows;
    });
  };

  // Add new step
  const addStep = (type: WorkflowType) => {
    const newStepName = `new_step_${Date.now()}`;
    const newStep = { color: "#808080" };

    setWorkflows(prev => {
      const newWorkflows = { ...prev };
      
      if (isGlobalMode) {
        // Add to all workflows in global mode
        const types: WorkflowType[] = ['objects', 'templates', 'documents', 'dialogs', 'loca'];
        types.forEach(t => {
          newWorkflows[t] = { ...newWorkflows[t], [newStepName]: newStep };
        });
      } else {
        // Add only to the selected type
        newWorkflows[type] = { ...newWorkflows[type], [newStepName]: newStep };
      }
      
      return newWorkflows;
    });
  };

  // Remove step
  const removeStep = (type: WorkflowType, stepKey: string) => {
    setWorkflows(prev => {
      const newWorkflows = { ...prev };
      
      if (isGlobalMode) {
        // Remove from all workflows in global mode
        const types: WorkflowType[] = ['objects', 'templates', 'documents', 'dialogs', 'loca'];
        types.forEach(t => {
          const workflow = { ...newWorkflows[t] };
          delete workflow[stepKey];
          newWorkflows[t] = workflow;
        });
      } else {
        // Remove only from the selected type
        const workflow = { ...newWorkflows[type] };
        delete workflow[stepKey];
        newWorkflows[type] = workflow;
      }
      
      return newWorkflows;
    });
  };

  // Check if there are changes
  const hasChanges = JSON.stringify(workflows) !== JSON.stringify(originalWorkflows);

  // Save changes
  const handleSave = async () => {
    if (!selectedCompany) {
      toast({
        title: "Error",
        description: "No company selected",
        variant: "destructive",
      });
      return;
    }

    try {
      if (statusConfig) {
        // Update existing configuration
        await updateStatus(statusConfig.id, workflows);
      } else {
        // Create new configuration
        await createStatus(
          selectedCompany.id,
          selectedGame?.id || null,
          workflows
        );
      }

      setOriginalWorkflows(JSON.parse(JSON.stringify(workflows)));

      toast({
        title: "Success",
        description: "Status workflows updated successfully",
      });
    } catch (error) {
      console.error("Error saving workflows:", error);
      toast({
        title: "Error",
        description: "Failed to save workflows",
        variant: "destructive",
      });
    }
  };

  // Cancel changes
  const handleCancel = () => {
    setWorkflows(JSON.parse(JSON.stringify(originalWorkflows)));
    setIsGlobalMode(checkAllWorkflowsIdentical(originalWorkflows));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const currentWorkflow = isGlobalMode ? workflows.objects : workflows[selectedType];
  const workflowEntries = Object.entries(currentWorkflow);

  return (
    <div className="max-w-4xl mx-left px-6 pt-4 pb-2">
      

      {/* Mode and Type Selectors */}
      <div className="flex items-center justify-left gap-4 p-4 rounded">
        <span className="text-sm font-medium text-foreground">Workflow Mode:</span>
        <Select
          value={isGlobalMode ? "global" : "specific"}
          onValueChange={(value) => {
            const shouldBeGlobal = value === "global";
            if (shouldBeGlobal !== isGlobalMode) {
              handleModeToggle();
            }
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="global">Global</SelectItem>
            <SelectItem value="specific">Specific</SelectItem>
          </SelectContent>
        </Select>

        {/* Type Selector (only in specific mode) */}
        {!isGlobalMode && (
          <>
            <span className="text-sm font-medium text-foreground">Workflow Type:</span>
            <Select
              value={selectedType}
              onValueChange={(value) => setSelectedType(value as WorkflowType)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue className="capitalize" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="objects">Objects</SelectItem>
                <SelectItem value="templates">Templates</SelectItem>
                <SelectItem value="documents">Documents</SelectItem>
                <SelectItem value="dialogs">Dialogs</SelectItem>
                <SelectItem value="loca">Loca</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {/* Workflow Editor */}
      <div className="max-w-4xl bg-card mx-left px-6 pt-4 pb-2 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-center flex-1 text-foreground">
            {isGlobalMode ? 'Global Workflow' : `${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} Workflow`}
          </h3>
          <Button
            onClick={() => addStep(selectedType)}
            size="iconSmall"
            variant="icon"
            className="gap-2"
          >
            <IconProvider iconstring="fa-plus" size={14} color="currentColor" />
          </Button>
        </div>

        <div className="space-y-3">
          {workflowEntries.map(([stepKey, stepValue]) => (
            <div key={stepKey} className="flex items-center gap-3 p-3  rounded">
              <Input
                type="text"
                value={stepKey}
                onChange={(e) => updateWorkflowStep(selectedType, stepKey, 'name', e.target.value)}
                className="flex-1 text-center"
                placeholder="Step name"
              />
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={stepValue.color}
                  onChange={(e) => updateWorkflowStep(selectedType, stepKey, 'color', e.target.value)}
                  className="w-12 h-10 rounded border border-input bg-background cursor-pointer"
                />
                <Input
                  type="text"
                  value={stepValue.color}
                  onChange={(e) => updateWorkflowStep(selectedType, stepKey, 'color', e.target.value)}
                  className="w-24 text-center"
                  placeholder="#000000"
                />
              </div>
              <Button
                onClick={() => removeStep(selectedType, stepKey)}
                variant="ghost"
                size="iconSmall"
                className="gap-2"
              >
                <IconProvider iconstring="fa-trash" size={14} />
              </Button>
            </div>
          ))}
          
          {workflowEntries.length === 0 && (
            <div className="text-center text-muted-foreground p-4">
              No steps defined. Click "Add Step" to create one.
            </div>
          )}
        </div>
      </div>

      {/* Save Bar */}
      {hasChanges && (
        <SaveBar
          isVisible={hasChanges}
          onSave={handleSave}
          onCancel={handleCancel}
          hasChanges={hasChanges}
        />
      )}
    </div>
  );
}

